import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, CheckCircle, FileVideo, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

const R2Upload = ({ onUploadComplete, folderPath: propFolderPath = 'tickets-evidence', disabled = false, maxFiles = 5, ticketContext = null }) => {
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    // Track progress for each file: { [fileName]: percentage }
    const [uploadProgress, setUploadProgress] = useState({});
    const [uploadedPaths, setUploadedPaths] = useState([]);

    const onDrop = useCallback((acceptedFiles) => {
        if (disabled) return;
        // Append new files, respecting maxFiles limit total
        setFiles(prev => {
            const newFiles = [...prev, ...acceptedFiles].slice(0, maxFiles);
            return newFiles;
        });
        setUploadedPaths([]); // Reset uploaded paths if new files are added (user intent to re-upload or add more)
    }, [disabled, maxFiles]);

    const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
        onDrop,
        maxFiles: maxFiles,
        maxSize: 500 * 1024 * 1024, // 500MB
        accept: {
            'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm'],
            'image/*': ['.png', '.jpg', '.jpeg', '.gif']
        },
        disabled: disabled,
        noClick: disabled,
        noKeyboard: disabled
    });

    // Handle rejections
    React.useEffect(() => {
        if (fileRejections.length > 0) {
            const rejection = fileRejections[0];
            if (rejection.errors[0].code === 'file-too-large') {
                toast.error(`O arquivo excede o limite de 500MB. Por favor, comprima o vídeo.`);
            } else if (rejection.errors[0].code === 'too-many-files') {
                toast.error(`Máximo de ${maxFiles} arquivo(s) permitido(s).`);
            } else {
                toast.error(rejection.errors[0].message);
            }
        }
    }, [fileRejections, maxFiles]);

    const uploadSingleFile = async (file) => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}.${fileExt}`;

        let currentFolderPath = propFolderPath;

        // If ticketContext is provided, construct the dynamic path
        // tickets-evidence > Discord ID > City > Ticket ID > [imagem_expira | video_expira]
        if (ticketContext && ticketContext.userId && ticketContext.ticketId) {
            const typeFolder = file.type.startsWith('video') ? 'video_expira' : 'imagem_expira';
            // Sanitize inputs to avoid doubles slashes or weird chars, though R2 handles most
            const safeCity = (ticketContext.city || 'Desconhecida').replace(/[^a-zA-Z0-9]/g, '_');
            currentFolderPath = `tickets-evidence/${ticketContext.userId}/${safeCity}/${ticketContext.ticketId}/${typeFolder}`;
        }

        try {
            // 1. Get Presigned URL
            const initRes = await fetch('/api/r2-upload-init', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fileName,
                    fileType: file.type,
                    folderPath: currentFolderPath
                })
            });

            if (!initRes.ok) {
                const err = await initRes.json().catch(() => ({}));
                throw new Error(err.error || `Failed to init upload: ${initRes.status} ${initRes.statusText}`);
            }

            const { uploadUrl, publicUrl, key } = await initRes.json();

            // 2. Upload to R2 (XHR for progress)
            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();

                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable) {
                        const percentComplete = (event.loaded / event.total) * 100;
                        setUploadProgress(prev => ({ ...prev, [file.name]: Math.round(percentComplete) }));
                    }
                };

                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve();
                    } else {
                        reject(new Error(`Upload failed with status ${xhr.status}`));
                    }
                };

                xhr.onerror = () => reject(new Error('Network error during upload (CORS or Connectivity)'));

                xhr.open('PUT', uploadUrl);
                xhr.setRequestHeader('Content-Type', file.type);
                // Important: Do not set custom headers that aren't in the signature (like x-amz-acl)
                xhr.send(file);
            });

            // Return publicUrl if available, otherwise return the R2 Key via Proxy
            if (publicUrl) return publicUrl;
            return `/api/r2-proxy?key=${encodeURIComponent(key)}`;
        } catch (error) {
            console.error(`Upload failed for ${file.name}:`, error);
            toast.error(`Erro ao enviar ${file.name}: ${error.message}`);
            return null;
        }
    };

    const handleUpload = async () => {
        if (files.length === 0 || disabled) return;

        setUploading(true);
        setUploadProgress({});

        try {
            const uploadPromises = files.map(file => uploadSingleFile(file));
            const results = await Promise.all(uploadPromises);

            // Filter out failures (nulls)
            const successfulUrls = results.filter(url => url !== null);

            if (successfulUrls.length > 0) {
                setUploadedPaths(successfulUrls);
                toast.success(`${successfulUrls.length} arquivo(s) enviado(s) com sucesso!`);

                // Safely call callback
                if (onUploadComplete) {
                    try {
                        onUploadComplete(successfulUrls);
                    } catch (cbError) {
                        console.error("Callback error:", cbError);
                        toast.error("Upload concluído, mas erro ao vincular prova.");
                    }
                }

                setFiles([]); // Clear files after successful upload to show success state
            }
        } catch (error) {
            console.error('Batch upload error:', error);
            toast.error('Erro durante o envio dos arquivos.');
        } finally {
            setUploading(false);
        }
    };

    const handleRemoveFile = (fileName) => {
        setFiles(prev => prev.filter(f => f.name !== fileName));
        setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[fileName];
            return newProgress;
        });
    };

    return (
        <div className="w-full space-y-4">
            {/* Dropzone Area */}
            <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200
                    ${disabled
                        ? 'border-white/5 bg-black/20 opacity-50 cursor-not-allowed'
                        : isDragActive
                            ? 'border-indigo-500 bg-indigo-500/10 cursor-pointer'
                            : 'border-white/10 hover:border-white/20 hover:bg-white/5 cursor-pointer'
                    }
                `}
            >
                <input {...getInputProps()} />
                <div className="flex flex-col items-center gap-3">
                    <div className={`p-3 rounded-full ${disabled ? 'bg-white/5' : 'bg-white/5'}`}>
                        {disabled ? (
                            <AlertCircle className="h-6 w-6 text-gray-500" />
                        ) : (
                            <Upload className="h-6 w-6 text-indigo-400" />
                        )}
                    </div>
                    <div className="space-y-1">
                        <p className={`font-medium ${disabled ? 'text-gray-500' : 'text-gray-200'}`}>
                            {disabled
                                ? 'Preencha o formulário primeiro'
                                : (isDragActive ? 'Solte os arquivos aqui...' : 'Clique ou arraste arquivos (Vídeo/Foto)')
                            }
                        </p>
                        {!disabled && (
                            <p className="text-sm text-gray-400">
                                Max {maxFiles} arquivo(s) (500MB cada)
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Selected Files List */}
            {files.length > 0 && (
                <div className="space-y-2">
                    {files.map((file, idx) => (
                        <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-center justify-between">
                            <div className="flex items-center gap-3 overflow-hidden">
                                <div className="p-2 rounded-lg bg-indigo-500/20 text-indigo-400 flex-shrink-0">
                                    {file.type.startsWith('video') ? <FileVideo className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
                                </div>
                                <div className="min-w-0">
                                    <p className="font-medium text-gray-200 text-sm truncate">{file.name}</p>
                                    <p className="text-xs text-gray-400">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                {uploading && (
                                    <span className="text-xs text-gray-400 font-mono">
                                        {uploadProgress[file.name] || 0}%
                                    </span>
                                )}
                                {!uploading && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={(e) => { e.stopPropagation(); handleRemoveFile(file.name); }}
                                        className="h-8 w-8 hover:bg-red-500/10 hover:text-red-400"
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}

                    {/* Overall Progress if needed, or just individual status above */}
                </div>
            )}

            {/* Upload Button */}
            {files.length > 0 && !uploading && uploadedPaths.length === 0 && (
                <Button
                    onClick={handleUpload}
                    disabled={disabled}
                    className={`w-full text-white ${disabled ? 'bg-gray-600 cursor-not-allowed opacity-50' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                >
                    {disabled ? 'Preencha o formulário para enviar' : `Confirmar Upload (${files.length})`}
                </Button>
            )}

            {/* Uploading State */}
            {uploading && (
                <Button disabled className="w-full bg-indigo-600/50 text-white cursor-not-allowed">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enviando arquivos...
                </Button>
            )}

            {/* Success Message & State */}
            {uploadedPaths.length > 0 && (
                <div className="space-y-3">
                    <div className="flex items-center gap-2 text-emerald-400 text-sm bg-emerald-500/10 p-3 rounded-lg justify-center animate-in fade-in border border-emerald-500/20">
                        <CheckCircle className="h-5 w-5" />
                        <span className="font-bold">Upload Concluído!</span>
                        <span>{uploadedPaths.length} arquivo(s) anexado(s).</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default R2Upload;
